import { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@mui/material'
import styled from '@emotion/styled'
import { DateTime } from 'luxon'

import { useMaybeUser } from 'src/hooks/use-user'
import { useProfile } from 'src/hooks/use-profile'
import { useRouting } from 'src/routes'
import { Button, ButtonVariant } from 'src/system/ui/button/Button'
import { RelativeLink } from 'src/system/ui/link/RelativeLink'
import { LoadingSpinner } from 'src/system/ui/LoadingSpinner'
import type { ProfileQuery } from 'src/graphql/types'
import {
  SupportContactButtons,
  SupportPrompt,
  SupportTrigger
} from 'src/screens/matches/MissingMatchesSupport'
import {
  AccountAnswer,
  BadgeAnswer,
  Outcome,
  Question,
  elsewhereModes,
  nextStep,
  officialModes,
  whenRanges
} from 'src/screens/matches/where-is-my-match'
import type {
  Answers,
  Context,
  Mode,
  Step
} from 'src/screens/matches/where-is-my-match'

const prefix = 'matches:where_is_my_match'

// Outcomes whose copy links to the account page (<1>…</1> in the string).
const accountLinkOutcomes = new Set([
  Outcome.AuthCodeMissing,
  Outcome.Faceit,
  Outcome.OtherPlatform,
  Outcome.BeforeJoined
])

// Replaces "Missing matches? Tell support" on the Valve tab: walks the user
// through the questions support would ask, ending at support if nothing fits.
export function WhereIsMyMatch() {
  const { t } = useTranslation()
  const [visible, setVisible] = useState(false)

  const open = () => setVisible(true)
  const close = () => setVisible(false)

  return (
    <>
      <SupportPrompt>
        {t('matches:tell_support:prompt')}{' '}
        <SupportTrigger type='button' onClick={open}>
          {t(`${prefix}:action`)}
        </SupportTrigger>
      </SupportPrompt>

      <Dialog open={visible} onClose={close} fullWidth maxWidth='sm'>
        {visible && <Triage onClose={close} />}
      </Dialog>
    </>
  )
}

function Triage(props: { onClose: () => void }) {
  const { profile, loading } = useProfile('cache-first')

  // The before-joining check needs createdAt: wait for the profile, or the
  // path could change under the user when it arrives mid-dialog.
  if (!profile && loading) {
    return (
      <DialogContent>
        <LoadingSpinner />
      </DialogContent>
    )
  }

  return <TriageSteps profile={profile} onClose={props.onClose} />
}

function TriageSteps(props: {
  profile: ProfileQuery['profile'] | undefined
  onClose: () => void
}) {
  const { t } = useTranslation()
  const { user } = useMaybeUser()
  const { profile } = props

  // Fixed when the dialog opens, so the answers can't drift under the user.
  const [now] = useState(() => DateTime.now())
  const [history, setHistory] = useState<Answers[]>([])
  const [answers, setAnswers] = useState<Answers>({})
  const [wantsSupport, setWantsSupport] = useState(false)

  const context: Context = {
    autoDownloadActive: user ? user.automatch.isActive : true,
    accountCreatedAt: profile?.createdAt
      ? DateTime.fromISO(profile.createdAt)
      : null,
    now
  }

  const step: Step = wantsSupport
    ? { kind: 'outcome', outcome: Outcome.Support }
    : nextStep(answers, context)

  const answer = (update: Answers) => {
    setHistory([...history, answers])
    setAnswers({ ...answers, ...update })
  }

  const back = () => {
    if (wantsSupport) {
      setWantsSupport(false)
      return
    }
    const previous = history[history.length - 1]
    if (!previous) return
    setHistory(history.slice(0, -1))
    setAnswers(previous)
  }

  const canGoBack = wantsSupport || history.length > 0
  const isSupport = step.kind === 'outcome' && step.outcome === Outcome.Support

  const summary = [
    answers.when &&
      t(`${prefix}:summary:when`, {
        value: t(`${prefix}:when:${answers.when}`)
      }),
    answers.mode &&
      t(`${prefix}:summary:mode`, {
        value: t(`${prefix}:mode:${answers.mode}`)
      }),
    user?.steamId &&
      t(`${prefix}:summary:steam`, {
        value: profile?.steam?.name
          ? `${profile.steam.name} (${user.steamId})`
          : user.steamId
      }),
    user?.automatch.checkedAt &&
      t(`${prefix}:summary:checked`, { value: user.automatch.checkedAt })
  ]
    .filter(Boolean)
    .join('\n')

  return (
    <>
      <DialogContent>
        <DialogTitle>{t(`${prefix}:title`)}</DialogTitle>
        {step.kind === 'question' ? (
          <QuestionStep
            question={step.question}
            onAnswer={answer}
            steamName={profile?.steam?.name ?? user?.steamId ?? ''}
            steamAvatarUrl={profile?.steam?.avatarUrl}
          />
        ) : (
          <OutcomeStep outcome={step.outcome} summary={summary} />
        )}
      </DialogContent>
      <StyledDialogActions>
        {canGoBack ? (
          <Button type='button' variant={ButtonVariant.text} onClick={back}>
            {t(`${prefix}:back`)}
          </Button>
        ) : (
          <span />
        )}
        <ActionsRight>
          {step.kind === 'outcome' && !isSupport && (
            <Button
              type='button'
              variant={ButtonVariant.link}
              onClick={() => setWantsSupport(true)}
            >
              {t(`${prefix}:still_stuck`)}
            </Button>
          )}
          {isSupport && <SupportContactButtons onContact={props.onClose} />}
        </ActionsRight>
      </StyledDialogActions>
    </>
  )
}

function QuestionStep(props: {
  question: Question
  onAnswer: (update: Answers) => void
  steamName: string
  steamAvatarUrl?: string | null
}) {
  const { t } = useTranslation()
  const { question, onAnswer } = props

  if (question === Question.When) {
    return (
      <>
        <QuestionText>{t(`${prefix}:questions:when`)}</QuestionText>
        <Options>
          {whenRanges.map((when) => (
            <Option key={when} onClick={() => onAnswer({ when })}>
              {t(`${prefix}:when:${when}`)}
            </Option>
          ))}
        </Options>
      </>
    )
  }

  if (question === Question.Mode) {
    const modeOptions = (modes: Mode[]) => (
      <Options twoColumns>
        {modes.map((mode) => (
          <Option key={mode} onClick={() => onAnswer({ mode })}>
            {t(`${prefix}:mode:${mode}`)}
          </Option>
        ))}
      </Options>
    )
    return (
      <>
        <QuestionText>{t(`${prefix}:questions:mode`)}</QuestionText>
        {modeOptions(officialModes)}
        <GroupLabel>{t(`${prefix}:questions:mode_elsewhere`)}</GroupLabel>
        {modeOptions(elsewhereModes)}
      </>
    )
  }

  if (question === Question.Account) {
    return (
      <>
        <QuestionText>{t(`${prefix}:questions:account`)}</QuestionText>
        <SteamAccount>
          {props.steamAvatarUrl && <Avatar src={props.steamAvatarUrl} alt='' />}
          <SteamName>{props.steamName}</SteamName>
        </SteamAccount>
        <Options>
          {Object.values(AccountAnswer).map((account) => (
            <Option key={account} onClick={() => onAnswer({ account })}>
              {t(`${prefix}:account:${account}`)}
            </Option>
          ))}
        </Options>
      </>
    )
  }

  return (
    <>
      <QuestionText>{t(`${prefix}:questions:badge`)}</QuestionText>
      <Options>
        {Object.values(BadgeAnswer).map((badge) => (
          <Option key={badge} onClick={() => onAnswer({ badge })}>
            {t(`${prefix}:badge:${badge}`)}
          </Option>
        ))}
      </Options>
    </>
  )
}

function OutcomeStep(props: { outcome: Outcome; summary: string }) {
  const { t } = useTranslation()
  const { routes } = useRouting()
  const key = `${prefix}:outcomes:${props.outcome}`

  return (
    <>
      <OutcomeTitle>{t(`${key}:title`)}</OutcomeTitle>
      <DialogContentText>
        {accountLinkOutcomes.has(props.outcome) ? (
          <Trans i18nKey={`${key}:body`}>
            {' '}
            <RelativeLink to={routes.account}> </RelativeLink>
          </Trans>
        ) : (
          t(`${key}:body`)
        )}
      </DialogContentText>
      {props.outcome === Outcome.Support && props.summary && (
        <Summary text={props.summary} />
      )}
    </>
  )
}

function Summary(props: { text: string }) {
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)

  function handleCopy() {
    try {
      const promise = navigator.clipboard?.writeText(props.text)
      if (!promise) {
        setCopied(false)
        return
      }

      promise.then(
        () => setCopied(true),
        () => setCopied(false)
      )
    } catch {
      setCopied(false)
    }
  }

  return (
    <SummaryBox>
      <SummaryHeader>
        <SummaryLabel>{t(`${prefix}:summary:label`)}</SummaryLabel>
        <CopyButton type='button' onClick={handleCopy}>
          {copied ? t(`${prefix}:summary:copied`) : t(`${prefix}:summary:copy`)}
        </CopyButton>
      </SummaryHeader>
      <SummaryText>{props.text}</SummaryText>
    </SummaryBox>
  )
}

function Option(props: { children: string; onClick: () => void }) {
  return (
    <Button
      type='button'
      variant={ButtonVariant.secondary}
      fullWidth
      onClick={props.onClick}
    >
      {props.children}
    </Button>
  )
}

const QuestionText = styled.p`
  margin: 0 0 ${(props) => props.theme.d.spacing[4]};
  font-size: 18px;
  color: ${(props) => props.theme.d.colors.text.secondary};
`

const GroupLabel = styled.p`
  margin: ${(props) => props.theme.d.spacing[5]} 0
    ${(props) => props.theme.d.spacing[3]};
  color: ${(props) => props.theme.d.colors.text.primary};
`

const Options = styled.div<{ twoColumns?: boolean }>`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${(props) => props.theme.d.spacing[2]};

  @media (min-width: ${(props) => props.theme.d.breakpoints.sm}) {
    grid-template-columns: repeat(
      ${(props) => (props.twoColumns ? 2 : 1)},
      1fr
    );
  }
`

const SteamAccount = styled.div`
  display: flex;
  align-items: center;
  gap: ${(props) => props.theme.d.spacing[3]};
  margin-bottom: ${(props) => props.theme.d.spacing[4]};
`

const Avatar = styled.img`
  width: 48px;
  height: 48px;
  border-radius: 4px;
`

const SteamName = styled.span`
  font-size: 18px;
  color: ${(props) => props.theme.d.colors.text.secondary};
`

const OutcomeTitle = styled.p`
  margin: 0 0 ${(props) => props.theme.d.spacing[3]};
  font-size: 18px;
  color: ${(props) => props.theme.d.colors.text.secondary};
`

const StyledDialogActions = styled(DialogActions)`
  justify-content: space-between;
`

const ActionsRight = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: ${(props) => props.theme.d.spacing[3]};
`

const SummaryBox = styled.div`
  margin-top: ${(props) => props.theme.d.spacing[4]};
`

const SummaryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${(props) => props.theme.d.spacing[1]};
`

const SummaryLabel = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${(props) => props.theme.d.colors.text.secondary};
`

const SummaryText = styled.pre`
  margin: 0;
  font-family: monospace;
  font-size: 14px;
  white-space: pre-wrap;
  word-break: break-word;
  color: ${(props) => props.theme.d.colors.text.primary};
`

const CopyButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-size: 14px;
  text-transform: uppercase;
  color: ${(props) => props.theme.d.colors.link.default};
`
